/**
 * EPL Admin - Resultados y Evaluaciones
 * Vista para ver resultados detallados de evaluaciones y feedback IA
 */

import React, { useState, useEffect } from 'react';
import { generateEPLExecutiveReport } from '../../utils/pdfGenerator';

// Utilidad para normalizar matrices tipo objeto a array de arrays
function normalizeMatrixData(data) {
  if (Array.isArray(data)) return data;
  if (typeof data === 'object' && data !== null) {
    // Detectar claves tipo "0-0", "1-2", etc.
    const keys = Object.keys(data);
    if (keys.length && keys[0].includes('-')) {
      // Obtener dimensiones
      const rows = Math.max(...keys.map(k => parseInt(k.split('-')[0], 10))) + 1;
      const cols = Math.max(...keys.map(k => parseInt(k.split('-')[1], 10))) + 1;
      const arr = Array.from({ length: rows }, () => Array(cols).fill(null));
      keys.forEach(k => {
        const [i, j] = k.split('-').map(Number);
        arr[i][j] = data[k];
      });
      return arr;
    }
  }
  return data;
}
import { 
  Search, 
  Filter, 
  Download, 
  Eye,
  FileText,
  Brain,
  Award,
  Calendar,
  User,
  FlaskConical,
  Trash2,
  MessageSquare,
  Send,
  X
} from 'lucide-react';
import { eplApi } from '../../services/epl_api';

const AdminResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLab, setFilterLab] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' o 'desc'
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  
  // Estados para modal de feedback docente
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    subject: '',
    message: '',
    sendEmail: true
  });
  const [sendingFeedback, setSendingFeedback] = useState(false);

  useEffect(() => {
    loadResults();
  }, [filterLab, filterUser]);

  const loadResults = async () => {
    try {
      setLoading(true);
      const response = await eplApi.getResults({
        search: searchTerm,
        labFilter: filterLab,
        userFilter: filterUser
      });
      setResults(response.data || []);
    } catch (error) {
      console.error('Error cargando resultados:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadResults();
  };

  const handleExportCSV = async () => {
    try {
      await eplApi.exportResultsCSV({
        search: searchTerm,
        labFilter: filterLab,
        userFilter: filterUser
      });
    } catch (error) {
      console.error('Error exportando CSV:', error);
      alert('Error al exportar CSV');
    }
  };

  const handleSortByDate = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Eliminar resultado
  const handleDeleteResult = async (resultId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este resultado? Esta acción no se puede deshacer.')) return;
    
    try {
      await eplApi.deleteResult(resultId);
      
      // Actualizar lista inmediatamente (optimistic update)
      setResults(prev => prev.filter(r => r.id !== resultId));
      
      // Cerrar modal si está abierto y es el resultado eliminado
      if (selectedResult?.id === resultId) {
        setSelectedResult(null);
        setShowDetailModal(false);
      }
      
      // Mostrar confirmación (opcional)
      // alert('Resultado eliminado correctamente');
    } catch (err) {
      console.error('Error al eliminar resultado:', err);
      alert('Error al eliminar el resultado: ' + (err.message || 'Error desconocido'));
    }
  };

  // Abrir modal de feedback docente
  const handleOpenFeedbackModal = (result) => {
    setSelectedResult(result);
    setFeedbackData({
      subject: `Feedback sobre: ${result.labName}`,
      message: '',
      sendEmail: true
    });
    setShowFeedbackModal(true);
  };

  // Enviar feedback docente
  const handleSendFeedback = async () => {
    if (!feedbackData.message.trim()) {
      alert('Por favor escribe un mensaje de feedback');
      return;
    }

    try {
      setSendingFeedback(true);
      
      const payload = {
        studentId: selectedResult.userId,
        labId: selectedResult.labId,
        sessionId: selectedResult.id || selectedResult._id || selectedResult.sessionId,
        subject: feedbackData.subject,
        message: feedbackData.message,
        sendEmail: feedbackData.sendEmail,
        labName: selectedResult.labName,
        labScore: selectedResult.score
      };
      
      console.log('📤 Enviando feedback con datos:', payload);
      await eplApi.sendTeacherFeedback(payload);

      alert('✅ Feedback enviado exitosamente');
      setShowFeedbackModal(false);
      setFeedbackData({ subject: '', message: '', sendEmail: true });
      
    } catch (error) {
      console.error('Error enviando feedback:', error);
      alert('Error al enviar feedback: ' + error.message);
    } finally {
      setSendingFeedback(false);
    }
  };

  // Descargar PDF del resultado
  const handleDownloadPDF = async (result) => {
    try {
      console.log('📄 Generando PDF para:', result.userName);
      console.log('🔍 labFields disponibles:', result.labFields);
      
      // Preparar respuestas del estudiante con información completa de los campos
      let responses = [];
      
      // Los datos vienen en result.answers, no result.responses
      if (result.answers && typeof result.answers === 'object') {
        responses = Object.entries(result.answers).map(([fieldLabel, value]) => {
          // Buscar el campo correspondiente para obtener premiumConfig (etiquetas de matriz)
          const field = result.labFields?.find(f => f.label === fieldLabel);
          
          console.log(`🔎 Campo "${fieldLabel}":`, {
            encontrado: !!field,
            premiumConfig: field?.premiumConfig,
            fieldType: field?.fieldType
          });
          
          return {
            fieldLabel: field?.label || key,
            fieldValue: value,
            fieldConfig: field?.premiumConfig ? {
              rowLabels: field.premiumConfig.rowLabels,
              colLabels: field.premiumConfig.columnLabels || field.premiumConfig.colLabels,
              fieldType: field.premiumConfig.fieldType
            } : null // Incluir config para matrices
          };
        });
      }
      
      console.log('📋 Respuestas procesadas con config:', responses);
      
      // Los datos de IA vienen en result.aiFeedback, no result.aiResults
      const aiResults = result.aiFeedback ? {
        score: result.aiFeedback.score || result.score,
        feedback: result.aiFeedback.feedback || '',
        recommendations: result.aiFeedback.recommendations || []
      } : {
        score: result.score || 0,
        feedback: 'Evaluación completada exitosamente.',
        recommendations: []
      };
      
      console.log('✅ Respuestas encontradas:', responses.length);
      console.log('✅ Feedback IA:', aiResults.feedback ? 'Disponible' : 'No disponible');
      
      // Preparar datos para el generador de PDF
      const pdfData = {
        labName: result.labName || 'Laboratorio EPL',
        userName: result.userName || 'N/A',
        userEmail: result.userEmail || 'N/A',
        systemCard: result.systemCard || 'innovafast',
        completedAt: result.completedAt,
        aiResults: aiResults,
        responses: responses,
        attempts: result.attempts || 0,
        timeSpent: 0
      };

      // Generar PDF usando la función compartida
      await generateEPLExecutiveReport(pdfData);
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF: ' + error.message);
    }
  };

  // Ordenar resultados por fecha
  const sortedResults = [...results].sort((a, b) => {
    const dateA = new Date(a.completedAt);
    const dateB = new Date(b.completedAt);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Paginación
  const totalPages = Math.ceil(sortedResults.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentResults = sortedResults.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset página al cambiar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterLab, filterUser]);

  const handleViewDetail = (result) => {
    setSelectedResult(result);
    setShowDetailModal(true);
  };

  const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSystemBadge = (systemCard) => {
    const badges = {
      innovafast: { label: 'InnovaFast', color: 'bg-blue-100 text-blue-700' },
      innovatech: { label: 'InnovaTech', color: 'bg-purple-100 text-purple-700' },
      transforma360: { label: 'Transforma360', color: 'bg-green-100 text-green-700' }
    };
    return badges[systemCard] || badges.innovafast;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-96 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Resultados y Evaluaciones</h2>
            <p className="text-xs sm:text-sm text-gray-600">Historial completo de evaluaciones con feedback IA</p>
          </div>
          <button 
            onClick={handleExportCSV}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors text-sm font-medium"
          >
            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Exportar CSV</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-2 sm:gap-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-9 sm:pl-10 pr-3 py-2 sm:py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <button
              onClick={handleSearch}
              className="px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors text-sm font-medium flex-shrink-0"
            >
              <span className="hidden sm:inline">Buscar</span>
              <Search className="w-4 h-4 sm:hidden" />
            </button>
          </div>

          <select
            value={filterLab}
            onChange={(e) => setFilterLab(e.target.value)}
            className="w-full px-3 py-2 sm:py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todos los labs</option>
          </select>
        </div>
      </div>

      {/* Results - Desktop: Tabla, Móvil: Cards */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Desktop Table - Hidden on mobile */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={handleSortByDate}
                >
                  <div className="flex items-center gap-2">
                    Fecha
                    <span className="text-gray-400">
                      {sortOrder === 'desc' ? '↓' : '↑'}
                    </span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Laboratorio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Intentos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Feedback IA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentResults.map((result) => {
                const systemBadge = getSystemBadge(result.systemCard);
                
                return (
                  <tr key={result.id} className="hover:bg-gray-50">
                    {/* Fecha - Primera columna */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {result.completedAt ? (
                        new Date(result.completedAt).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      ) : (
                        <span className="text-gray-400">Sin fecha</span>
                      )}
                    </td>
                    
                    {/* Usuario */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                          {result.userName.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{result.userName}</div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Laboratorio */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{result.labName}</div>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${systemBadge.color}`}>
                        {systemBadge.label}
                      </span>
                    </td>
                    
                    {/* Score */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-lg font-bold ${getScoreColor(result.score, result.maxScore)}`}>
                        {result.score}/{result.maxScore}
                      </div>
                    </td>
                    
                    {/* Intentos */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {result.attempts !== null 
                          ? `${result.attempts}/${result.maxAttempts}` 
                          : <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">N/D</span>
                        }
                      </div>
                    </td>
                    
                    {/* Feedback IA */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {result.hasAiFeedback ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <Brain className="w-4 h-4" />
                          <span className="text-sm font-medium">Disponible</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No disponible</span>
                      )}
                    </td>
                    
                    {/* Acciones */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetail(result)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver detalle"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenFeedbackModal(result)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Enviar feedback docente"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(result)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Descargar PDF"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteResult(result.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Eliminar resultado"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards - Hidden on desktop */}
        <div className="lg:hidden">
          {/* Sort header para móvil */}
          <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase">Resultados</span>
            <button
              onClick={handleSortByDate}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 font-medium"
            >
              <Calendar className="w-3 h-3" />
              Fecha {sortOrder === 'desc' ? '↓' : '↑'}
            </button>
          </div>

          <div className="divide-y divide-gray-200">
            {currentResults.map((result) => {
              const systemBadge = getSystemBadge(result.systemCard);
              
              return (
                <div key={result.id} className="p-3 hover:bg-gray-50 transition-colors">
                  {/* Header de la card */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                      {result.userName.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{result.userName}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {result.completedAt ? (
                          new Date(result.completedAt).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        ) : 'Sin fecha'}
                      </div>
                    </div>
                    <div className={`text-xl font-bold ${getScoreColor(result.score, result.maxScore)} flex-shrink-0`}>
                      {result.score}/{result.maxScore}
                    </div>
                  </div>

                  {/* Lab info */}
                  <div className="mb-3">
                    <div className="text-sm font-medium text-gray-900 mb-1 line-clamp-1">{result.labName}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${systemBadge.color}`}>
                        {systemBadge.label}
                      </span>
                      {result.attempts !== null && (
                        <span className="text-xs text-gray-600">
                          {result.attempts}/{result.maxAttempts} intentos
                        </span>
                      )}
                      {result.hasAiFeedback && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <Brain className="w-3 h-3" />
                          IA
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleViewDetail(result)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Ver
                    </button>
                    <button
                      onClick={() => handleOpenFeedbackModal(result)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Mensaje
                    </button>
                    <button
                      onClick={() => handleDownloadPDF(result)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="PDF"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteResult(result.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">
            Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a{' '}
            <span className="font-medium">{Math.min(indexOfLastItem, sortedResults.length)}</span> de{' '}
            <span className="font-medium">{sortedResults.length}</span> resultados
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Mostrar solo páginas cercanas a la actual
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPage - 3 ||
                  page === currentPage + 3
                ) {
                  return (
                    <span key={page} className="px-2 text-gray-500">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                    Detalle de Evaluación
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{selectedResult.userName}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FlaskConical className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{selectedResult.labName}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="whitespace-nowrap">
                        {new Date(selectedResult.completedAt).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          year: window.innerWidth >= 640 ? 'numeric' : undefined
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Score */}
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">PUNTUACIÓN</p>
                    <p className={`text-3xl sm:text-4xl font-bold ${getScoreColor(selectedResult.score, selectedResult.maxScore)}`}>
                      {selectedResult.score}/{selectedResult.maxScore}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">INTENTOS</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {selectedResult.attempts !== null 
                        ? `${selectedResult.attempts}/${selectedResult.maxAttempts}`
                        : <span className="text-sm sm:text-base px-2 py-1 rounded-full bg-gray-100 text-gray-600">N/D</span>
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Answers */}
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Respuestas del Estudiante</h4>
                {selectedResult.answers && Object.keys(selectedResult.answers).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(selectedResult.answers).map(([question, answer], idx) => {
                      // Si la respuesta es matriz tipo objeto, normalizar
                      const normalized = normalizeMatrixData(answer);
                      if (Array.isArray(normalized) && Array.isArray(normalized[0])) {
                        // Renderizar como tabla
                        return (
                          <div key={idx} className="border border-gray-200 rounded-lg p-4 overflow-x-auto">
                            <p className="font-medium text-gray-900 mb-2">{idx + 1}. {question}</p>
                            <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                              <tbody>
                                {normalized.map((row, i) => (
                                  <tr key={i}>
                                    {row.map((cell, j) => (
                                      <td key={j} className="border px-3 py-2 text-gray-700">
                                        {typeof cell === 'object' && cell !== null
                                          ? <pre className="text-xs bg-gray-50 rounded p-1 overflow-x-auto">{JSON.stringify(cell, null, 2)}</pre>
                                          : String(cell)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      } else if (typeof normalized === 'object' && normalized !== null) {
                        // Renderizar objeto plano como JSON legible
                        return (
                          <div key={idx} className="border border-gray-200 rounded-lg p-4">
                            <p className="font-medium text-gray-900 mb-2">{idx + 1}. {question}</p>
                            <pre className="text-gray-700 bg-gray-50 rounded p-2 text-xs overflow-x-auto">{JSON.stringify(normalized, null, 2)}</pre>
                          </div>
                        );
                      } else {
                        // Primitivo: mostrar como texto
                        return (
                          <div key={idx} className="border border-gray-200 rounded-lg p-4">
                            <p className="font-medium text-gray-900 mb-2">{idx + 1}. {question}</p>
                            {/* Si el valor es un objeto, mostrarlo como JSON legible */}
                            {typeof normalized === 'object' && normalized !== null
                              ? <pre className="text-xs bg-gray-50 rounded p-1 overflow-x-auto">{JSON.stringify(normalized, null, 2)}</pre>
                              : <p className="text-gray-700 whitespace-pre-wrap">{String(normalized)}</p>}
                          </div>
                        );
                      }
                    })}
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-gray-500">No hay respuestas registradas para este laboratorio</p>
                  </div>
                )}
              </div>

              {/* AI Feedback */}
              {selectedResult.hasAiFeedback && selectedResult.aiFeedback && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Brain className="w-5 h-5 text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Feedback de IA</h4>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-gray-800 leading-relaxed">{selectedResult.aiFeedback.feedback}</p>
                  </div>

                  {selectedResult.aiFeedback.recommendations && selectedResult.aiFeedback.recommendations.length > 0 && (
                    <div>
                      <p className="font-medium text-gray-900 mb-2">Recomendaciones:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {selectedResult.aiFeedback.recommendations.map((rec, idx) => (
                          <li key={idx} className="text-gray-700">{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-3 sm:p-6">
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors text-sm font-medium"
                >
                  Cerrar
                </button>
                <button
                  onClick={() => handleDownloadPDF(selectedResult)}
                  className="flex-1 px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Descargar PDF</span>
                  <span className="sm:hidden">PDF</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 sm:p-6 rounded-t-2xl sm:rounded-t-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-xl font-bold leading-tight">Enviar Feedback Docente</h2>
                    <p className="text-purple-100 text-xs sm:text-sm mt-0.5">Mensaje personalizado para el estudiante</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="text-white/80 hover:text-white transition-colors flex-shrink-0 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Información del destinatario */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{selectedResult.userName}</div>
                    <div className="text-xs sm:text-sm text-gray-600 truncate">{selectedResult.userEmail}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-700 mt-2 sm:mt-3">
                  <FlaskConical className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 flex-shrink-0" />
                  <span className="font-medium truncate">{selectedResult.labName}</span>
                  <span className="text-gray-400">•</span>
                  <Award className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-600 flex-shrink-0" />
                  <span className="font-semibold text-yellow-600">{selectedResult.score}/100</span>
                </div>
              </div>

              {/* Campo Asunto */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Asunto
                </label>
                <input
                  type="text"
                  value={feedbackData.subject}
                  onChange={(e) => setFeedbackData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 sm:py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Asunto del mensaje..."
                />
              </div>

              {/* Campo Mensaje */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Mensaje *
                </label>
                <textarea
                  value={feedbackData.message}
                  onChange={(e) => setFeedbackData(prev => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  className="w-full px-3 py-2 sm:py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  placeholder="Escribe tu feedback personalizado aquí...&#10;&#10;Puedes incluir:&#10;• Puntos fuertes del trabajo&#10;• Áreas de mejora&#10;• Recomendaciones específicas&#10;• Próximos pasos"
                />
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">
                  {feedbackData.message.length}/5000 caracteres
                </div>
              </div>

              {/* Opciones */}
              <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={feedbackData.sendEmail}
                  onChange={(e) => setFeedbackData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="sendEmail" className="text-sm text-gray-700 flex items-center gap-2">
                  <Send className="w-4 h-4 text-blue-600" />
                  <span>También enviar por correo electrónico</span>
                </label>
              </div>

              {/* Preview del mensaje */}
              {feedbackData.message && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="text-xs font-semibold text-gray-500 mb-2">PREVIEW</div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {feedbackData.message}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-3 sm:p-6 flex gap-2 sm:gap-3">
              <button
                onClick={() => setShowFeedbackModal(false)}
                disabled={sendingFeedback}
                className="flex-1 px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors disabled:opacity-50 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendFeedback}
                disabled={sendingFeedback || !feedbackData.message.trim()}
                className="flex-1 px-4 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:bg-purple-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
              >
                {sendingFeedback ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span className="hidden sm:inline">Enviando...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span className="hidden sm:inline">Enviar Feedback</span>
                    <span className="sm:hidden">Enviar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 text-sm text-gray-600">
        Mostrando {results.length} resultado{results.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default AdminResults;
